import { normalize, resolve, type OpenOptions, type Vfs } from "idfweb-vfs";

import { DEV, type Device } from "./devices.ts";
import { Errno, WasiError } from "./errno.ts";
import { OpenFile, Pipe } from "./shared.ts";

/** File descriptor table implementation for WASIX processes over idfweb-vfs. */

export const Oflags = { CREATE: 1, DIRECTORY: 2, EXCL: 4, TRUNC: 8 } as const;
export const Fdflags = { APPEND: 1, DSYNC: 2, NONBLOCK: 4, RSYNC: 8, SYNC: 16 } as const;
export const Fdflagsext = { CLOEXEC: 1 } as const;
export const Whence = { SET: 0, CUR: 1, END: 2 } as const;
export const Lookupflags = { SYMLINK_FOLLOW: 1 } as const;
/** Timestamp flags for filestat_set_times. */
export const Fstflags = { ATIM: 1, ATIM_NOW: 2, MTIM: 4, MTIM_NOW: 8 } as const;

export const Rights = {
    FD_READ: 1n << 1n,
    FD_WRITE: 1n << 6n,
    ALL: (1n << 63n) - 1n,
} as const;

/** Preopened guest directory path. */
export type Preopen = string;

export { OpenFile, Pipe };

export type FdEntry =
    | { kind: "stdio"; which: number }
    /** A file in /dev whose reads and writes the host answers. */
    | { kind: "device"; device: Device }
    | { kind: "dir"; guestPath: string; preopen: boolean }
    | { kind: "file"; guestPath: string; file: OpenFile; fdflags: number }
    | { kind: "pipe"; pipe: Pipe; end: "read" | "write"; fdflags: number };

/**
 * File descriptors 0-2 are stdio; preopens begin at descriptor 3. `/dev` is
 * the last preopen of every table, so that libc finds a directory for it.
 */
export const FIRST_PREOPEN_FD = 3;

export class FdTable {
    readonly #vfs: Vfs;
    readonly #preopens: Preopen[];
    readonly #entries = new Map<number, FdEntry>();
    /** Set of file descriptors marked close-on-exec. */
    readonly #cloexec = new Set<number>();
    /** Reference count of descriptors in this process pointing to each OpenFile. */
    readonly #files = new Map<OpenFile, number>();

    constructor(vfs: Vfs, preopens: Preopen[]) {
        this.#vfs = vfs;
        this.#preopens = preopens.map((p) => normalize(p));
        if (!this.#preopens.includes(DEV)) this.#preopens.push(DEV);
        for (const which of [0, 1, 2]) this.#entries.set(which, { kind: "stdio", which });
        this.#preopens.forEach((guestPath, i) =>
            this.#entries.set(FIRST_PREOPEN_FD + i, { kind: "dir", guestPath, preopen: true }),
        );
    }

    get preopens(): readonly Preopen[] {
        return this.#preopens;
    }

    get(fd: number): FdEntry {
        const entry = this.#entries.get(fd);
        if (entry === undefined) throw new WasiError(Errno.BADF);
        return entry;
    }

    file(fd: number): OpenFile {
        const entry = this.get(fd);
        if (entry.kind !== "file") throw new WasiError(Errno.BADF);
        return entry.file;
    }

    /** The lowest unused descriptor at or above `min`. */
    #free(min: number): number {
        let fd = Math.max(min, 0);
        while (this.#entries.has(fd)) fd++;
        return fd;
    }

    /** Insert an entry into the lowest available descriptor at or above `min`. */
    insert(entry: FdEntry, min = 0): number {
        const fd = this.#free(min);
        this.#entries.set(fd, entry);
        this.#retain(entry);
        return fd;
    }

    dup(fd: number, min = 0, cloexec = false): number {
        const copy = this.insert(this.get(fd), min);
        if (cloexec) this.#cloexec.add(copy);
        return copy;
    }

    /** Move an entry to a specific descriptor, closing any existing target descriptor. */
    renumber(from: number, to: number): void {
        const entry = this.get(from);
        if (from === to) return;
        if (this.#entries.has(to)) this.close(to);
        this.#entries.delete(from);
        this.#entries.set(to, entry);
        if (this.#cloexec.delete(from)) this.#cloexec.add(to);
    }

    cloexec(fd: number): boolean {
        this.get(fd);
        return this.#cloexec.has(fd);
    }

    setCloexec(fd: number, on: boolean): void {
        this.get(fd);
        if (on) this.#cloexec.add(fd);
        else this.#cloexec.delete(fd);
    }

    close(fd: number): void {
        const entry = this.get(fd);
        this.#entries.delete(fd);
        this.#cloexec.delete(fd);
        this.#release(entry);
    }

    /** Close all open file descriptors in this table. */
    closeAll(): void {
        for (const fd of [...this.#entries.keys()]) {
            try {
                this.close(fd);
            } catch {
                // Ignore errors during batch shutdown.
            }
        }
    }

    /** Duplicate the descriptor table for a spawned child process. */
    fork(): FdTable {
        const child = new FdTable(this.#vfs, this.#preopens);
        child.#entries.clear();
        for (const [fd, entry] of this.#entries) {
            child.#entries.set(fd, entry);
            child.#retain(entry);
            if (this.#cloexec.has(fd)) child.#cloexec.add(fd);
        }
        for (const file of child.#files.keys()) this.#vfs.retain(file.handle);
        return child;
    }

    /** Close descriptors marked close-on-exec. */
    closeCloexec(): void {
        for (const fd of [...this.#cloexec]) this.close(fd);
    }

    /** Serialize the table and shared memory handles for worker transfer. */
    snapshot(): FdSnapshot {
        return {
            preopens: [...this.#preopens],
            entries: [...this.#entries].map(([fd, entry]) => [fd, serialize(entry)]),
            cloexec: [...this.#cloexec],
        };
    }

    /** Reconstruct a descriptor table from a serialized snapshot. */
    static fromSnapshot(vfs: Vfs, snapshot: FdSnapshot): FdTable {
        return FdTable.#over(vfs, snapshot, false);
    }

    /** Reconstruct a descriptor table from a snapshot for a retry attempt. */
    static copyOfSnapshot(vfs: Vfs, snapshot: FdSnapshot): FdTable {
        return FdTable.#over(vfs, snapshot, true);
    }

    static #over(vfs: Vfs, snapshot: FdSnapshot, again: boolean): FdTable {
        const table = new FdTable(vfs, snapshot.preopens);
        table.#entries.clear();
        const shared = new Map<SharedArrayBuffer, Pipe | OpenFile>();
        for (const [fd, serialized] of snapshot.entries) {
            const entry = deserialize(serialized, shared);
            table.#entries.set(fd, entry);
            if (again) table.#retain(entry);
            else table.#count(entry);
        }
        if (again) for (const file of table.#files.keys()) vfs.retain(file.handle);
        for (const fd of snapshot.cloexec) table.#cloexec.add(fd);
        return table;
    }

    /** Note one more descriptor on whatever the entry refers to, here and elsewhere. */
    #retain(entry: FdEntry): void {
        this.#count(entry);
        if (entry.kind === "pipe") retainPipe(entry);
    }

    /** The part of it that is this table's alone. */
    #count(entry: FdEntry): void {
        if (entry.kind === "file") {
            this.#files.set(entry.file, (this.#files.get(entry.file) ?? 0) + 1);
        }
    }

    /** The reverse, giving the file back once this process holds no descriptor on it. */
    #release(entry: FdEntry): void {
        if (entry.kind === "file") {
            const left = this.#files.get(entry.file)! - 1;
            if (left > 0) {
                this.#files.set(entry.file, left);
            } else {
                this.#files.delete(entry.file);
                this.#vfs.close(entry.file.handle);
            }
        } else if (entry.kind === "pipe") {
            releasePipe(entry);
        }
    }

    /**
     * Turn a (directory fd, path) pair into a guest path this program is
     * allowed to reach.
     *
     * The check goes through the preopen list rather than through the directory
     * entry: a path that normalises its way out of every preopen has nowhere to
     * land and is refused with `outside`, whatever fd it arrived with.
     */
    resolve(dirfd: number, path: string, outside: Errno = Errno.NOTCAPABLE): string {
        const dir = this.get(dirfd);
        if (dir.kind !== "dir") throw new WasiError(Errno.NOTDIR);
        return resolveInPreopens(this.#preopens, resolve(dir.guestPath, path), outside);
    }
}

/**
 * A guest path, normalised, if the preopens allow it to be reached at all.
 *
 * Separate from FdTable because it is also how a path with no directory fd
 * behind it gets checked: the executable given in a proc_spawn3, and the
 * working directory a chdir asks for.
 *
 * A path outside every preopen throws `outside`: ENOENT for a lookup, since
 * nothing exists there for the guest and clang only tries its next include
 * directory on "not found"; ENOTCAPABLE for a change.
 */
export function resolveInPreopens(
    preopens: readonly Preopen[],
    path: string,
    outside: Errno = Errno.NOTCAPABLE,
): string {
    const guestPath = normalize(path);
    for (const preopen of preopens) {
        if (under(normalize(preopen), guestPath)) return guestPath;
    }
    throw new WasiError(outside);
}

/**
 * Note one more end open on a pipe.
 *
 * Counted across every process rather than only in one table, because what a
 * reader waits for is the last writer anywhere going away.
 */
function retainPipe(entry: FdEntry & { kind: "pipe" }): void {
    if (entry.end === "read") entry.pipe.addReader(1);
    else entry.pipe.addWriter(1);
}

function releasePipe(entry: FdEntry & { kind: "pipe" }): void {
    if (entry.end === "read") entry.pipe.addReader(-1);
    else entry.pipe.addWriter(-1);
}

/** Serialized descriptor table state transferred across worker threads. */
export interface FdSnapshot {
    preopens: Preopen[];
    entries: [number, SerializedEntry][];
    cloexec: number[];
}

export type SerializedEntry =
    | { kind: "stdio"; which: number }
    | { kind: "device"; device: Device }
    | { kind: "dir"; guestPath: string; preopen: boolean }
    | { kind: "file"; guestPath: string; sab: SharedArrayBuffer; fdflags: number }
    | { kind: "pipe"; sab: SharedArrayBuffer; end: "read" | "write"; fdflags: number };

function serialize(entry: FdEntry): SerializedEntry {
    switch (entry.kind) {
        case "file":
            return {
                kind: "file",
                guestPath: entry.guestPath,
                sab: entry.file.sab,
                fdflags: entry.fdflags,
            };
        case "pipe":
            return { kind: "pipe", sab: entry.pipe.sab, end: entry.end, fdflags: entry.fdflags };
        default:
            return entry;
    }
}

/** Release file and pipe references held by a snapshot when a worker dies before startup. */
export function releaseSnapshot(vfs: Vfs, snapshot: FdSnapshot): void {
    const shared = new Map<SharedArrayBuffer, Pipe | OpenFile>();
    const files = new Set<OpenFile>();
    for (const [, serialized] of snapshot.entries) {
        const entry = deserialize(serialized, shared);
        if (entry.kind === "pipe") releasePipe(entry);
        else if (entry.kind === "file") files.add(entry.file);
    }
    for (const file of files) vfs.close(file.handle);
}

function deserialize(serialized: SerializedEntry, shared: Map<SharedArrayBuffer, Pipe | OpenFile>): FdEntry {
    const attach = <T extends Pipe | OpenFile>(sab: SharedArrayBuffer, make: () => T): T => {
        let object = shared.get(sab);
        if (object === undefined) shared.set(sab, (object = make()));
        return object as T;
    };
    switch (serialized.kind) {
        case "file":
            return {
                kind: "file",
                guestPath: serialized.guestPath,
                fdflags: serialized.fdflags,
                file: attach(serialized.sab, () => OpenFile.attach(serialized.sab)),
            };
        case "pipe":
            return {
                kind: "pipe",
                end: serialized.end,
                fdflags: serialized.fdflags,
                pipe: attach(serialized.sab, () => Pipe.attach(serialized.sab)),
            };
        default:
            return serialized;
    }
}

/** Check whether `path` equals or is located inside `base`. */
function under(base: string, path: string): boolean {
    if (path === base) return true;
    return path.startsWith(base.endsWith("/") ? base : base + "/");
}

/** Map WASI open flags, rights, and descriptor flags to VFS OpenOptions. */
export function openOptions(oflags: number, rights: bigint, fdflags: number): OpenOptions {
    const write = (rights & Rights.FD_WRITE) !== 0n;
    return {
        read: !write || (rights & Rights.FD_READ) !== 0n,
        write,
        create: (oflags & Oflags.CREATE) !== 0,
        exclusive: (oflags & Oflags.EXCL) !== 0,
        truncate: (oflags & Oflags.TRUNC) !== 0,
        append: (fdflags & Fdflags.APPEND) !== 0,
    };
}
